import type { ReferralStatus } from '@clareefai/types';
import { Avatar, toast } from '@clareefai/ui';
import {
  ArrowLeft,
  Award,
  Building,
  Calendar,
  ChevronDown,
  Clock,
  ExternalLink,
  Gift,
  Loader2,
  Mail,
  Phone,
  Send,
  ShieldX,
  Star,
  ThumbsDown,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PageTitle from '../../components/shared/PageTitle';
import {
  useGetVendorReferralByIdQuery,
  useUpdateReferralStatusMutation,
  useConfirmReferralInvitationMutation,
  useAssignReferralMutation,
  usePushReferralToCrmMutation,
  useApproveReferralRewardMutation,
  useResendReferralInvitationMutation,
} from '../../store/vendorApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';

const STATUS_CONFIG: Record<ReferralStatus, { labelKey: string; color: string; icon: React.ElementType }> = {
  PENDING: { labelKey: 'referrals.status.pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  CONTACTED: { labelKey: 'referrals.status.contacted', color: 'bg-blue-100 text-blue-800', icon: Phone },
  QUALIFIED: { labelKey: 'referrals.status.qualified', color: 'bg-green-100 text-green-800', icon: Star },
  DISQUALIFIED: { labelKey: 'referrals.status.disqualified', color: 'bg-red-100 text-red-800', icon: ShieldX },
  REGISTERED: { labelKey: 'referrals.status.registered', color: 'bg-teal-100 text-teal-800', icon: Users },
  WON: { labelKey: 'referrals.status.won', color: 'bg-emerald-100 text-emerald-800', icon: Award },
  LOST: { labelKey: 'referrals.status.lost', color: 'bg-gray-100 text-gray-600', icon: ThumbsDown },
};

const ReferralDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: referral, isLoading, error } = useGetVendorReferralByIdQuery(id!, { skip: !id });
  const { data: teamMembers = [] } = useGetTeamMembersQuery();
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateReferralStatusMutation();
  const [confirmInvitation, { isLoading: isConfirming }] = useConfirmReferralInvitationMutation();
  const [assignReferral, { isLoading: isAssigning }] = useAssignReferralMutation();
  const [pushToCrm, { isLoading: isPushing }] = usePushReferralToCrmMutation();
  const [approveReward, { isLoading: isApproving }] = useApproveReferralRewardMutation();
  const [resendInvitation, { isLoading: isResending }] = useResendReferralInvitationMutation();

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusChange = async (status: ReferralStatus) => {
    if (!id) return;
    try {
      await updateStatus({ id, status }).unwrap();
      toast.success(t('referrals.detail.statusUpdated', { status: t(STATUS_CONFIG[status].labelKey) }));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.detail.statusUpdateFailed'));
    }
    setStatusDropdownOpen(false);
  };

  const handleConfirmInvitation = async () => {
    if (!id) return;
    try {
      await confirmInvitation(id).unwrap();
      toast.success(t('referrals.detail.invitationSentToast'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.detail.invitationSendFailed'));
    }
  };

  const handleResendInvitation = async () => {
    if (!id) return;
    try {
      await resendInvitation(id).unwrap();
      toast.success(t('referrals.detail.invitationResent'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.detail.invitationResendFailed'));
    }
  };

  const handleAssign = async (teamMemberId: string) => {
    if (!id) return;
    try {
      await assignReferral({ id, team_member_id: teamMemberId }).unwrap();
      toast.success(t('referrals.detail.referralAssigned'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.detail.referralAssignFailed'));
    }
  };

  const handlePushToCrm = async (provider: 'hubspot' | 'salesforce') => {
    if (!id) return;
    try {
      await pushToCrm({ id, provider }).unwrap();
      toast.success(t(provider === 'hubspot' ? 'referrals.detail.pushedToHubSpot' : 'referrals.detail.pushedToSalesforce'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.detail.pushFailed'));
    }
  };

  const handleApproveReward = async () => {
    if (!id) return;
    try {
      await approveReward(id).unwrap();
      toast.success(t('referrals.detail.rewardApproved'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.detail.rewardApproveFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-gray-500">{t('referrals.detail.notFound')}</p>
        <button onClick={() => navigate('/referrals')} className="mt-4 text-purple-600 hover:underline text-sm">
          {t('referrals.detail.backToReferrals')}
        </button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[referral.status];
  const StatusIcon = statusConfig.icon;
  const statuses: ReferralStatus[] = ['PENDING', 'CONTACTED', 'QUALIFIED', 'DISQUALIFIED', 'WON', 'LOST'];
  const showSendInvitation = !referral.vendor_confirmed_at;
  const showResendInvitation = !!referral.vendor_confirmed_at && !referral.registered_at;
  const showApproveReward = !referral.reward_id && referral.reward_trigger && referral.reward_trigger !== 'LEAD_SUBMISSION';

  return (
    <div className="space-y-6">
      <PageTitle title={`${referral.first_name} ${referral.last_name}`} />

      {/* Back button */}
      <button
        onClick={() => navigate('/referrals')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('referrals.detail.backToReferrals')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar name={`${referral.first_name} ${referral.last_name}`} size="lg" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {referral.first_name} {referral.last_name}
              </h1>
              <p className="text-gray-500">{referral.email}</p>
              {referral.company && (
                <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                  <Building className="w-3.5 h-3.5" />
                  {referral.company}
                </p>
              )}
            </div>
          </div>

          {/* Status badge + dropdown */}
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-4 h-4" />
              {t(statusConfig.labelKey)}
            </span>
            <div className="relative">
              <button
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                disabled={isUpdatingStatus}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('referrals.changeStatus')}
              >
                {isUpdatingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {statusDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border z-20 py-1">
                    {statuses.map((status) => {
                      const config = STATUS_CONFIG[status];
                      const Icon = config.icon;
                      return (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(status)}
                          disabled={status === referral.status}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${status === referral.status ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Icon className="w-4 h-4" />
                          {t(config.labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Info */}
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('referrals.detail.leadInfo')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{referral.email}</span>
              </div>
              {referral.linkedin && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                  <a href={referral.linkedin} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                    {t('referrals.detail.linkedinProfile')}
                  </a>
                </div>
              )}
              {referral.company && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{referral.company}</span>
                </div>
              )}
              {referral.company_size && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{t('referrals.detail.employees', { count: referral.company_size })}</span>
                </div>
              )}
            </div>
            {referral.sectors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">{t('referrals.detail.sectors')}</p>
                <div className="flex flex-wrap gap-2">
                  {referral.sectors.map((sector) => (
                    <span key={sector} className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {referral.notes && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{t('referrals.detail.notes')}</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{referral.notes}</p>
              </div>
            )}
            <div className="pt-2 border-t">
              <div className="flex items-center gap-3">
                <Avatar name={referral.promoter_name || t('referrals.detail.unknown')} size="sm" />
                <div>
                  <div className="text-sm font-medium text-gray-900">{referral.promoter_name || t('referrals.detail.unknown')}</div>
                  <div className="text-xs text-gray-500">{t('referrals.detail.referredByPromoter')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          {referral.status_history.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('referrals.detail.statusTimeline')}</h2>
              <div className="space-y-4">
                {[...referral.status_history].reverse().map((change, index) => {
                  const toConfig = STATUS_CONFIG[change.to_status];
                  const ToIcon = toConfig.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${toConfig.color}`}>
                        <ToIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {t(STATUS_CONFIG[change.from_status].labelKey)} → {t(toConfig.labelKey)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {change.changed_by_name && `${t('referrals.detail.byUser', { name: change.changed_by_name })} · `}
                          {formatDate(change.timestamp)}
                        </p>
                        {change.note && (
                          <p className="text-sm text-gray-600 mt-1 bg-gray-50 rounded p-2">{change.note}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar actions */}
        <div className="space-y-6">
          {/* AE Assignment */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
              <UserPlus className="w-4 h-4 inline mr-1" />
              {t('referrals.detail.assignToTeamMember')}
            </h3>
            {referral.assigned_to_name ? (
              <div className="flex items-center gap-2 mb-3">
                <Avatar name={referral.assigned_to_name} size="sm" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{referral.assigned_to_name}</p>
                  {referral.assigned_at && (
                    <p className="text-xs text-gray-500">{t('referrals.detail.assignedAt', { date: formatDate(referral.assigned_at) })}</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-3">{t('referrals.detail.notAssignedYet')}</p>
            )}
            <select
              onChange={(e) => {
                if (e.target.value) handleAssign(e.target.value);
                e.target.value = '';
              }}
              disabled={isAssigning}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              defaultValue=""
            >
              <option value="" disabled>{isAssigning ? t('referrals.detail.assigning') : t('referrals.detail.selectTeamMember')}</option>
              {teamMembers.map((tm) => (
                <option key={tm.id} value={tm.id}>
                  {tm.firstName} {tm.lastName} ({tm.role})
                </option>
              ))}
            </select>
          </div>

          {/* Invitation */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
              <Send className="w-4 h-4 inline mr-1" />
              {t('referrals.detail.prospectInvitation')}
            </h3>
            {referral.vendor_confirmed_at ? (
              <div className="space-y-2">
                <p className="text-sm text-green-700 bg-green-50 rounded-lg p-2">
                  {t('referrals.detail.invitationSentOn', { date: formatDate(referral.vendor_confirmed_at) })}
                </p>
                {showResendInvitation && (
                  <button
                    onClick={handleResendInvitation}
                    disabled={isResending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    {isResending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    {t('referrals.detail.resendInvitation')}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">{t('referrals.detail.noInvitationYet')}</p>
                {showSendInvitation && (
                  <button
                    onClick={handleConfirmInvitation}
                    disabled={isConfirming}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {isConfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {t('referrals.detail.sendInvitation')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* CRM Push */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
              {t('referrals.detail.crmIntegration')}
            </h3>
            {referral.crm_pushed_at ? (
              <div className="space-y-2">
                <p className="text-sm text-green-700 bg-green-50 rounded-lg p-2">
                  {t('referrals.detail.pushedTo', { provider: referral.crm_provider === 'hubspot' ? 'HubSpot' : referral.crm_provider === 'salesforce' ? 'Salesforce' : referral.crm_provider, date: formatDate(referral.crm_pushed_at) })}
                </p>
                {referral.hubspot_contact_id && (
                  <p className="text-xs text-gray-500">{t('referrals.detail.contactId', { id: referral.hubspot_contact_id })}</p>
                )}
                {referral.hubspot_deal_id && (
                  <p className="text-xs text-gray-500">{t('referrals.detail.dealId', { id: referral.hubspot_deal_id })}</p>
                )}
                {referral.salesforce_lead_id && (
                  <p className="text-xs text-gray-500">{t('referrals.detail.leadId', { id: referral.salesforce_lead_id })}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => handlePushToCrm('hubspot')}
                  disabled={isPushing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {isPushing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('referrals.detail.pushToHubSpot')}
                </button>
                <button
                  onClick={() => handlePushToCrm('salesforce')}
                  disabled={isPushing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#00A1E0] text-white rounded-lg hover:bg-[#0070A8] transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {isPushing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('referrals.detail.pushToSalesforce')}
                </button>
              </div>
            )}
          </div>

          {/* Approve Reward */}
          {showApproveReward && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                <Gift className="w-4 h-4 inline mr-1" />
                {t('referrals.detail.rewardApproval')}
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                {t('referrals.detail.rewardTriggerLabel')}: <span className="font-medium">{referral.reward_trigger?.replace(/_/g, ' ').toLowerCase()}</span>
              </p>
              <button
                onClick={handleApproveReward}
                disabled={isApproving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                {t('referrals.detail.approveLeadReward')}
              </button>
            </div>
          )}

          {/* Dates */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('referrals.detail.timeline')}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('referrals.detail.created')}</span>
                <span className="text-gray-700">{formatDate(referral.created_at)}</span>
              </div>
              {referral.contacted_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.contacted')}</span>
                  <span className="text-gray-700">{formatDate(referral.contacted_at)}</span>
                </div>
              )}
              {referral.qualified_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.qualified')}</span>
                  <span className="text-gray-700">{formatDate(referral.qualified_at)}</span>
                </div>
              )}
              {referral.vendor_confirmed_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.invitationSent')}</span>
                  <span className="text-gray-700">{formatDate(referral.vendor_confirmed_at)}</span>
                </div>
              )}
              {referral.registered_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.registered')}</span>
                  <span className="text-gray-700">{formatDate(referral.registered_at)}</span>
                </div>
              )}
              {referral.won_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.won')}</span>
                  <span className="text-gray-700">{formatDate(referral.won_at)}</span>
                </div>
              )}
              {referral.lost_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.lost')}</span>
                  <span className="text-gray-700">{formatDate(referral.lost_at)}</span>
                </div>
              )}
              {referral.disqualified_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('referrals.detail.disqualified')}</span>
                  <span className="text-gray-700">{formatDate(referral.disqualified_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralDetail;
